import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  ClipboardList,
  Loader2,
  MessageSquarePlus,
  RotateCcw,
  Send,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  type IncidentNoteDto,
  type IncidentSeverity,
  type IncidentStatus,
  useAddIncidentNote,
  useIncident,
  useUpdateIncident,
} from '../api/useIncidents.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';
import { formatDateTime, formatRelative } from '../lib/format.js';
import { SeverityChip, StatusChip } from './Incidents.js';

const NEXT_STATUS: Partial<
  Record<IncidentStatus, { to: IncidentStatus; label: string; tone: 'primary' | 'secondary' }[]>
> = {
  open: [
    { to: 'under_review', label: 'Start review', tone: 'primary' },
    { to: 'closed', label: 'Close', tone: 'secondary' },
    { to: 'cancelled', label: 'Cancel', tone: 'secondary' },
  ],
  under_review: [
    { to: 'closed', label: 'Close', tone: 'primary' },
    { to: 'open', label: 'Re-open', tone: 'secondary' },
  ],
  closed: [{ to: 'open', label: 'Re-open', tone: 'secondary' }],
  cancelled: [{ to: 'open', label: 'Re-open', tone: 'secondary' }],
};

export default function IncidentDetail() {
  const { id } = useParams<{ id: string }>();
  const query = useIncident(id);
  const update = useUpdateIncident();
  const addNote = useAddIncidentNote(id ?? '');
  const [noteBody, setNoteBody] = useState('');

  if (query.isLoading) {
    return <div className="mx-auto max-w-4xl text-sm text-ink-faint">Loading incident…</div>;
  }
  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-4xl">
        <EmptyState
          icon={ClipboardList}
          title="Incident not found"
          description={
            (query.error as Error | undefined)?.message ?? 'This incident may have been removed.'
          }
        />
        <div className="mt-4">
          <Link to="/incidents" className="text-sm text-brand-purple hover:underline">
            ← Back to incidents
          </Link>
        </div>
      </div>
    );
  }

  const { incident, notes } = query.data;
  const transitions = NEXT_STATUS[incident.status] ?? [];

  async function submitNote(e: React.FormEvent) {
    e.preventDefault();
    if (!noteBody.trim()) return;
    await addNote.mutateAsync(noteBody);
    setNoteBody('');
  }

  async function changeStatus(next: IncidentStatus) {
    if (!id) return;
    await update.mutateAsync({ id, status: next });
  }

  async function changeSeverity(next: IncidentSeverity) {
    if (!id || next === incident.severity) return;
    await update.mutateAsync({ id, severity: next });
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Link
        to="/incidents"
        className="inline-flex items-center gap-1 text-sm text-ink-faint hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to incidents
      </Link>

      <div className="panel">
        <div className="panel-body">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <span className="eyebrow">Incident</span>
              <div className="mt-1 flex items-center gap-2.5">
                <ClipboardList className="h-5 w-5 text-brand-purple" />
                <h1 className="font-heading text-[22px] font-bold tracking-tightest text-ink">
                  {incident.title}
                </h1>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-faint">
                <StatusChip status={incident.status} />
                <SeverityChip severity={incident.severity} />
                {incident.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[10px] uppercase tracking-eyebrow-tight text-ink-faint bg-surface-3 px-1.5 py-0.5 rounded-full"
                  >
                    {t}
                  </span>
                ))}
                <span>·</span>
                <span>Opened {formatRelative(incident.created_at)}</span>
                {incident.created_by_display_name && (
                  <>
                    <span>·</span>
                    <span>by {incident.created_by_display_name}</span>
                  </>
                )}
                {incident.closed_at && (
                  <>
                    <span>·</span>
                    <span>Closed {formatRelative(incident.closed_at)}</span>
                  </>
                )}
              </div>
              {incident.alarm_id && (
                <Link
                  to={`/alarms/${incident.alarm_id}`}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 transition hover:bg-red-100"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  View linked alarm →
                </Link>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {transitions.map((t) => (
                <button
                  key={t.to}
                  type="button"
                  disabled={update.isPending}
                  onClick={() => changeStatus(t.to)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50',
                    t.tone === 'primary'
                      ? 'bg-brand-purple text-white hover:bg-brand-deep-purple'
                      : 'border border-line bg-white text-ink-dim hover:bg-surface-3',
                  )}
                >
                  {t.to === 'closed' ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : t.to === 'cancelled' ? (
                    <Ban className="h-3.5 w-3.5" />
                  ) : t.to === 'open' ? (
                    <RotateCcw className="h-3.5 w-3.5" />
                  ) : null}
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 border-t border-line pt-4 sm:grid-cols-3">
            <Fact label="Affected worker" value={incident.affected_user_display_name ?? '—'} />
            <Fact label="Opened" value={formatDateTime(incident.created_at)} />
            <Fact
              label="Last update"
              value={formatDateTime(incident.updated_at ?? incident.created_at)}
            />
          </div>

          <div className="mt-4 rounded-md bg-surface-3 p-4">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-ink-faint">
              Description
            </div>
            <p className="whitespace-pre-wrap text-sm text-ink">{incident.description}</p>
          </div>

          <div className="mt-4 flex items-center gap-2 border-t border-line pt-3">
            <span className="text-[11px] font-medium uppercase tracking-wider text-ink-faint">
              Severity
            </span>
            <div className="flex flex-wrap gap-1">
              {(['low', 'medium', 'high', 'critical'] as IncidentSeverity[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => changeSeverity(s)}
                  disabled={update.isPending}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize',
                    incident.severity === s
                      ? 'border-brand-purple bg-brand-purple text-white'
                      : 'border-line bg-white text-ink-dim hover:border-line-strong',
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {update.isError && (
        <div className="inline-flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {(update.error as Error).message}
        </div>
      )}

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-ink">
              <MessageSquarePlus className="h-4 w-4 text-brand-purple" />
              Timeline
            </h2>
            <span className="eyebrow">Notes · status changes</span>
          </div>
        </div>
        <div className="panel-body">
          <ul className="flex flex-col gap-3 border-l-2 border-line pl-4">
            {notes.map((n) => (
              <TimelineEntry key={n.id} note={n} />
            ))}
            {notes.length === 0 && (
              <li className="py-2 text-sm text-ink-faint">No timeline entries yet.</li>
            )}
          </ul>

          <form
            onSubmit={submitNote}
            className="mt-4 flex flex-col gap-2 border-t border-line pt-4"
          >
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Add a note</span>
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                rows={3}
                maxLength={5000}
                placeholder="Follow-up actions, manager comments, return-to-work notes…"
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
            </label>
            {addNote.isError && (
              <div className="inline-flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-800">
                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                {(addNote.error as Error).message}
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!noteBody.trim() || addNote.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
              >
                {addNote.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Post note
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TimelineEntry({ note }: { note: IncidentNoteDto }) {
  const isStatusChange = note.entry_type === 'status_change';
  const isSeverityChange = note.entry_type === 'severity_change';
  const isAuto = isStatusChange || isSeverityChange;
  return (
    <li className="relative">
      <span
        className={cn(
          'absolute -left-[22px] top-1.5 inline-flex h-3 w-3 items-center justify-center rounded-full',
          isAuto ? 'bg-gray-300' : 'bg-brand-purple',
        )}
      />
      <div className="flex items-center gap-2 text-[11px] text-ink-faint">
        <span>{note.actor_display_name ?? 'system'}</span>
        <span>·</span>
        <span>{formatRelative(note.created_at)}</span>
        <span>·</span>
        <span className="text-[10px] text-ink-mute">{formatDateTime(note.created_at)}</span>
      </div>
      <div className={cn('mt-0.5 text-sm', isAuto ? 'italic text-ink-faint' : 'text-ink')}>
        {note.body}
      </div>
    </li>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-ink-faint">{label}</div>
      <div className="mt-0.5 text-sm text-ink">{value}</div>
    </div>
  );
}
